import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { vbCrypt, vbDecrypt } from "../../utils/functions";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Postgres error codes we turn into friendly messages
const PG_UNIQUE_VIOLATION = "23505";
const PG_FOREIGN_KEY_VIOLATION = "23503";

const EMPTY_FORM = {
    executive_id: null,
    executive_name: "",
    mobile: "",
    address: "",
    login_name: "",
    password: "",
};

const Executives = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [decrypting, setDecrypting] = useState(false);

    // ── Pagination ───────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data: executives = [], loading, fetchAll: fetchExecutives, save: saveExecutive, remove: deleteExecutive } = useGeneric({
        tableName: "executives",
        idColumn: "executive_id",
        orderBy: "executive_name",
        removeOnInsert: ["executive_id"],
    });

    useEffect(() => { fetchExecutives(); }, [fetchExecutives]);
    useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        const name = form.executive_name.trim();
        const login = form.login_name.trim();

        if (!name) errs.executive_name = "Name is required.";
        else if (name.length > 50) errs.executive_name = "Max 50 characters.";

        if (!form.mobile.trim()) errs.mobile = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile.trim())) errs.mobile = "Mobile must be 10 digits.";

        if (!login) errs.login_name = "Login name is required.";
        else if (login.length > 50) errs.login_name = "Max 50 characters.";
        else if (/\s/.test(login)) errs.login_name = "Login name cannot contain spaces.";
        else if (
            (executives || []).some(
                (e) =>
                    Number(e.executive_id) !== Number(form.executive_id) &&
                    (e.login_name ?? "").toLowerCase() === login.toLowerCase()
            )
        ) errs.login_name = "This login name is already used.";

        if (!form.password.trim()) errs.password = "Password is required.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const encryptedPassword = await vbCrypt(form.password);
            if (!encryptedPassword) {
                // vbCrypt returns "" if the browser cannot encrypt (e.g. page not served over HTTPS).
                // Saving that would create an executive nobody can log in as.
                alert("Could not encrypt the password. Please open the site over HTTPS and try again.");
                return;
            }
            const payload = {
                ...form,
                executive_name: form.executive_name.trim(),
                mobile: form.mobile.trim(),
                address: form.address.trim(),
                login_name: form.login_name.trim(),
                password: encryptedPassword,
            };
            if (!payload.executive_id) delete payload.executive_id;
            await saveExecutive(payload);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            if (err?.code === PG_UNIQUE_VIOLATION) {
                setErrors((prev) => ({ ...prev, login_name: "This login name is already used." }));
            } else {
                alert("Save failed: " + (err.message || "Unknown error"));
            }
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => { setForm(EMPTY_FORM); setErrors({}); setShowForm(true); };

    const openEdit = async (exec) => {
        setDecrypting(true);
        setShowForm(true);

        // Decrypt the stored password before showing it (same as the Users screen)
        let plainPassword = "";
        try {
            if (exec.password) plainPassword = await vbDecrypt(exec.password);
        } catch {
            plainPassword = "";
        }

        setForm({
            executive_id: exec.executive_id,
            executive_name: exec.executive_name ?? "",
            mobile: exec.mobile ?? "",
            address: exec.address ?? "",
            login_name: exec.login_name ?? "",
            password: plainPassword,
        });
        setErrors({});
        setDecrypting(false);
    };

    const closeModal = () => { setShowForm(false); setForm(EMPTY_FORM); setErrors({}); setDecrypting(false); };

    const handleDelete = async (exec, e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete executive "${exec.executive_name}"?`)) return;
        try {
            await deleteExecutive(exec.executive_id);
        } catch (err) {
            if (err?.code === PG_FOREIGN_KEY_VIOLATION) {
                alert(
                    `"${exec.executive_name}" cannot be deleted because they are already recorded on leads, ` +
                    `service calls or activities.`
                );
            } else {
                alert("Delete failed: " + (err.message || "Unknown error"));
            }
        }
    };

    // ── Filter & sort ────────────────────────────────────────────────
    const q = search.trim().toLowerCase();
    const filtered = (executives || [])
        .filter((x) =>
            !q ||
            (x.executive_name ?? "").toLowerCase().includes(q) ||
            (x.mobile ?? "").includes(q) ||
            (x.login_name ?? "").toLowerCase().includes(q) ||
            (x.address ?? "").toLowerCase().includes(q)
        )
        .sort((a, b) => (a.executive_name ?? "").localeCompare(b.executive_name ?? ""));

    // ── Paginate ─────────────────────────────────────────────────────
    const totalRecords = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const paginated = filtered.slice(startIdx, startIdx + pageSize);

    const goTo = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const getPageNumbers = () => {
        const pages = [];
        const left = Math.max(1, safePage - 2);
        const right = Math.min(totalPages, safePage + 2);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="cl-page">
            <div className="cl-panel">
                {/* ── Heading ── */}
                <div className="cl-panel-heading">
                    <span>Executive Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Executive">
                        <Plus size={16} />
                    </button>
                </div>

                {/* ── Search ── */}
                <div className="cl-search-bar">
                    <input type="text" placeholder="Search by name, mobile, login name, address…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="cl-search-input" />
                    <button className="cl-refresh-btn" onClick={fetchExecutives} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <>
                        <div className="cl-table-wrapper">
                            <table className="cl-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Executive ID</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Login Name</th>
                                        <th>Address</th>
                                        <th className="cl-actions-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr><td colSpan={7} className="cl-empty">No executives found.</td></tr>
                                    ) : (
                                        paginated.map((x, i) => (
                                            <tr
                                                key={x.executive_id ?? i}
                                                className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                                onClick={() => openEdit(x)}
                                                title="Click to edit"
                                            >
                                                <td data-label="#">{startIdx + i + 1}</td>
                                                <td data-label="Executive ID">{x.executive_id}</td>
                                                <td data-label="Name">{x.executive_name}</td>
                                                <td data-label="Mobile">{x.mobile}</td>
                                                <td data-label="Login Name">{x.login_name}</td>
                                                <td data-label="Address">{x.address}</td>
                                                <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                    <button className="cl-icon-btn cl-edit" onClick={() => openEdit(x)} title="Edit">
                                                        <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                    </button>
                                                    <button className="cl-icon-btn cl-delete" onClick={(e) => handleDelete(x, e)} title="Delete">
                                                        <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pagination ── */}
                        <div className="cl-pagination">
                            <div className="cl-pagination-info">
                                {totalRecords === 0 ? "No records" : `Showing ${startIdx + 1}–${Math.min(startIdx + pageSize, totalRecords)} of ${totalRecords}`}
                            </div>
                            <div className="cl-pagination-controls">
                                <button className="cl-page-btn" onClick={() => goTo(1)} disabled={safePage === 1} title="First"><ChevronsLeft size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1} title="Previous"><ChevronLeft size={15} /></button>
                                {getPageNumbers()[0] > 1 && (
                                    <><button className="cl-page-btn" onClick={() => goTo(1)}>1</button>
                                        {getPageNumbers()[0] > 2 && <span className="cl-page-ellipsis">…</span>}</>
                                )}
                                {getPageNumbers().map((p) => (
                                    <button key={p} className={`cl-page-btn ${p === safePage ? "cl-page-active" : ""}`} onClick={() => goTo(p)}>{p}</button>
                                ))}
                                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                                    <>{getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && <span className="cl-page-ellipsis">…</span>}
                                        <button className="cl-page-btn" onClick={() => goTo(totalPages)}>{totalPages}</button></>
                                )}
                                <button className="cl-page-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages} title="Next"><ChevronRight size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(totalPages)} disabled={safePage === totalPages} title="Last"><ChevronsRight size={15} /></button>
                            </div>
                            <div className="cl-pagination-size">
                                <label>Rows:</label>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal (closes only via the X / Close button, never by clicking outside) ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">{form.executive_id ? "Edit Executive" : "Add Executive"}</h4>
                            <button className="cl-modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className="cl-modal-body">
                            {decrypting ? (
                                <div className="cl-loading" style={{ padding: "40px 0", textAlign: "center" }}>
                                    Decrypting password…
                                </div>
                            ) : (
                                <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                                    {/* Name | Executive ID */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-10">
                                            <label className="cl-control-label">Name</label>
                                            <input type="text"
                                                className={`cl-form-control${errors.executive_name ? " cl-input-error" : ""}`}
                                                placeholder="Executive Name" maxLength={50}
                                                value={form.executive_name}
                                                onChange={(e) => set("executive_name", e.target.value)} />
                                            {errors.executive_name && <span className="cl-error-msg">{errors.executive_name}</span>}
                                        </div>
                                        <div className="cl-col-2">
                                            <label className="cl-control-label">ID</label>
                                            <input type="text" className="cl-form-control cl-readonly"
                                                placeholder="Auto" value={form.executive_id ?? ""} readOnly />
                                        </div>
                                    </div>

                                    {/* Mobile | Login name */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-6">
                                            <label className="cl-control-label">Mobile</label>
                                            <input type="tel"
                                                className={`cl-form-control${errors.mobile ? " cl-input-error" : ""}`}
                                                placeholder="Mobile Number" maxLength={10}
                                                value={form.mobile}
                                                onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} />
                                            {errors.mobile && <span className="cl-error-msg">{errors.mobile}</span>}
                                        </div>
                                        <div className="cl-col-6">
                                            <label className="cl-control-label">Login Name</label>
                                            <input type="text"
                                                className={`cl-form-control${errors.login_name ? " cl-input-error" : ""}`}
                                                placeholder="Login Name" maxLength={50}
                                                autoComplete="off"
                                                value={form.login_name}
                                                onChange={(e) => set("login_name", e.target.value)} />
                                            {errors.login_name && <span className="cl-error-msg">{errors.login_name}</span>}
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-12">
                                            <label className="cl-control-label">
                                                Password
                                                {form.executive_id && (
                                                    <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                                                        (decrypted)
                                                    </span>
                                                )}
                                            </label>
                                            <input type="text"
                                                className={`cl-form-control${errors.password ? " cl-input-error" : ""}`}
                                                placeholder="Password" maxLength={50}
                                                autoComplete="off"
                                                value={form.password}
                                                onChange={(e) => set("password", e.target.value)} />
                                            {errors.password && <span className="cl-error-msg">{errors.password}</span>}
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-12">
                                            <label className="cl-control-label">Address</label>
                                            <textarea
                                                className="cl-form-control"
                                                rows={3}
                                                placeholder="Address"
                                                style={{ resize: "vertical", fontFamily: "inherit" }}
                                                value={form.address}
                                                onChange={(e) => set("address", e.target.value)} />
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>

                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>Close</button>
                            <button className="cl-btn cl-btn-primary" onClick={onSave} disabled={saving || decrypting}>
                                {saving ? "Saving…" : form.executive_id ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Executives;
